import React, { useEffect, useState } from "react";
import { apiGet } from "../utils/api";
import { FaCheck, FaArrowLeft } from "react-icons/fa";
import { Spinner, PageHeader } from "../components/ui";
import { Link } from "react-router-dom";

// INR pricing overlay: maps planKey + interval to INR price and checkout URL
const INR_PRICES = {
  // Monthly
  "starter|month": {
    base: 460, gst: 82.80, total: 542.80,
    url: "https://www.digitaldomination.co.in/checkout?product=walytic-starter-monthly&price=460",
  },
  "pro|month": {
    base: 1104, gst: 198.72, total: 1302.72,
    url: "https://www.digitaldomination.co.in/checkout?product=walytic-professional-monthly&price=1104",
  },
  "business|month": {
    base: 2668, gst: 480.24, total: 3148.24,
    url: "https://www.digitaldomination.co.in/checkout?product=walytic-business-monthly&price=2668",
  },
  // Yearly
  "starter|year": {
    base: 4416, gst: 794.88, total: 5210.88, monthlyEquiv: 368,
    url: "https://www.digitaldomination.co.in/checkout?product=walytic-starter-yearly&price=4416",
  },
  "pro|year": {
    base: 11040, gst: 1987.20, total: 13027.20, monthlyEquiv: 920,
    url: "https://www.digitaldomination.co.in/checkout?product=walytic-professional-yearly&price=11040",
  },
  "business|year": {
    base: 25392, gst: 4570.56, total: 29962.56, monthlyEquiv: 2116,
    url: "https://www.digitaldomination.co.in/checkout?product=walytic-business-yearly&price=25392",
  },
  // Lifetime
  "lifetimestarter|lifetime": {
    base: 9108, gst: 1639.44, total: 10747.44,
    url: "https://www.digitaldomination.co.in/checkout?product=walytic-lifetime-starter&price=9108",
  },
  "lifetimepro|lifetime": {
    base: 18308, gst: 3295.44, total: 21603.44,
    url: "https://www.digitaldomination.co.in/checkout?product=walytic-lifetime-pro&price=18308",
  },
};

export default function PricingINR() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState("lifetime");

  useEffect(() => {
    apiGet("/api/plans")
      .then((res) => {
        setPlans(res?.plans || res?.data?.plans || []);
      })
      .catch((e) => console.error("Failed to load plans:", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="page-content flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  const hasYearlyPricing = plans.some((p) => p.prices?.some((pr) => pr.interval === "year" && pr.amount > 0));
  const hasLifetimePricing = plans.some((p) => p.prices?.some((pr) => pr.interval === "lifetime" && pr.amount > 0));

  // Filter plans that have INR pricing for the selected interval
  const visiblePlans = plans.filter((plan) => {
    if (plan.isFree) return false; // no free plan in INR
    const key = `${plan.key}|${interval}`;
    return !!INR_PRICES[key];
  });

  return (
    <div className="page-content">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/pricing" className="text-ink-muted hover:text-ink-primary transition-colors">
          <FaArrowLeft size={14} />
        </Link>
        <PageHeader
          title="Plans in INR"
          subtitle="Pay in Indian Rupees via secure checkout. All prices include 18% GST."
        />
      </div>

      {/* Billing Toggle: Lifetime first, then Yearly. Monthly is moved below the cards. */}
      <div className="flex items-center justify-center mb-8">
        <div className="inline-flex items-center rounded-lg border border-surface-border bg-surface-bg p-1 gap-1">
          {hasLifetimePricing && (
            <button
              type="button"
              onClick={() => setInterval("lifetime")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                interval === "lifetime" ? "bg-brand text-white shadow-sm" : "text-ink-muted hover:text-ink-primary"
              }`}
            >
              Lifetime
              <span className="ml-1.5 text-[10px] opacity-80">Best value</span>
            </button>
          )}
          {hasYearlyPricing && (
            <button
              type="button"
              onClick={() => setInterval("year")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                interval === "year" ? "bg-brand text-white shadow-sm" : "text-ink-muted hover:text-ink-primary"
              }`}
            >
              Yearly
              <span className="ml-1.5 text-[10px] opacity-80">Save more</span>
            </button>
          )}
        </div>
      </div>

      <div className={`grid grid-cols-1 ${visiblePlans.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-3"} gap-5 mb-6`}>
        {visiblePlans.map((plan) => {
          const isPopular = plan.badge;
          const inr = INR_PRICES[`${plan.key}|${interval}`];

          return (
            <div
              key={plan.id}
              className={`card relative overflow-hidden transition-all ${
                isPopular ? "border-brand/50 ring-1 ring-brand/20" : ""
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0">
                  <span className="badge-brand text-xs px-3 py-1 rounded-bl-lg rounded-tr-lg">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="p-6">
                <h3 className="text-lg font-semibold text-ink-primary mb-1">{plan.name}</h3>
                <p className="text-sm text-ink-muted mb-4">{plan.description}</p>

                <div className="mb-2">
                  <span className="text-4xl font-extrabold tracking-tighter text-ink-primary">
                    ₹{inr.base.toLocaleString("en-IN")}
                  </span>
                  {interval === "lifetime" ? (
                    <span className="text-ink-muted text-sm"> one-time</span>
                  ) : (
                    <span className="text-ink-muted text-sm">
                      /{interval === "year" ? "year" : "month"}
                    </span>
                  )}
                </div>

                <p className="text-xs text-ink-faint mb-1">
                  + GST ₹{inr.gst.toLocaleString("en-IN")} = <strong>₹{inr.total.toLocaleString("en-IN")}</strong>
                </p>

                {interval === "year" && inr.monthlyEquiv && (
                  <p className="text-xs text-ink-faint mb-4">
                    ₹{inr.monthlyEquiv.toLocaleString("en-IN")}/mo billed annually
                  </p>
                )}
                {interval === "lifetime" && (
                  <p className="text-xs text-ink-faint mb-4">Pay once, use forever</p>
                )}
                {interval === "month" && <div className="mb-4" />}

                <a
                  href={inr.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${isPopular ? "btn-primary" : "btn-secondary"} w-full mb-6 text-center block`}
                >
                  Get Started
                </a>

                {/* Features — directly from the plan, same as USD pricing page */}
                {plan.features?.length > 0 && (() => {
                  const moduleGroups = {};
                  plan.features.forEach((f) => {
                    const mod = f.module || "General";
                    if (!moduleGroups[mod]) moduleGroups[mod] = [];
                    moduleGroups[mod].push(f);
                  });
                  return (
                    <div className="space-y-4">
                      {Object.entries(moduleGroups).sort(([a], [b]) => a.localeCompare(b)).map(([mod, modFeatures]) => (
                        <div key={mod}>
                          <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">{mod}</p>
                          <div className="space-y-2">
                            {modFeatures.map((f, i) => (
                              <div key={i} className="flex items-start gap-2.5">
                                <FaCheck size={12} className="text-brand mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-ink-secondary">
                                  {f.label || f.key}
                                  {typeof f.value === "boolean"
                                    ? null
                                    : <>: {f.value === -1 || f.value === "-1"
                                        ? "Unlimited"
                                        : Number(f.value)?.toLocaleString() || f.value}</>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* De-emphasized monthly switch. Anchors conversion to lifetime/yearly. */}
      <div className="flex justify-center pb-4">
        {interval !== "month" ? (
          <button
            type="button"
            onClick={() => setInterval("month")}
            className="text-sm text-ink-muted hover:text-ink-primary underline-offset-4 hover:underline transition-colors"
          >
            Prefer monthly billing? Switch to monthly →
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setInterval(hasLifetimePricing ? "lifetime" : "year")}
            className="text-sm text-ink-muted hover:text-ink-primary underline-offset-4 hover:underline transition-colors"
          >
            ← Save more with yearly or lifetime plans
          </button>
        )}
      </div>
    </div>
  );
}
